import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "scopeSelect", "b2bFilters", "b2cFilters", "previewCount",
    "campaignSelect", "campaignSection", "criteriaSection",
    "modeSelector", "modeCardCampaign", "modeCardCriteria",
    "modeRadioCampaign", "modeRadioCriteria",
    "nameInput", "enrichButton"
  ]

  connect() {
    this._debounceTimer = null
    this._autoName = !this.hasNameInputTarget || this.nameInputTarget.value.trim() === ""
    // Highlight initial mode card selected (style visuel persistant)
    this._refreshModeCardStyles()
  }

  disconnect() {
    clearTimeout(this._debounceTimer)
  }

  nameEdited() {
    this._autoName = false
  }

  scopeChanged() {
    const scope = this.scopeSelectTarget.value
    this.b2bFiltersTarget.classList.toggle("hidden", scope !== "b2b")
    this.b2cFiltersTarget.classList.toggle("hidden", scope !== "b2c")
    this.generateName()
    this.schedulePreview()
  }

  criteriaChanged() {
    this.generateName()
    this.schedulePreview()
  }

  // Retour Sandrine (10/06/2026) — bouton "Tout cocher / décocher" des fieldsets
  // multi-sélection (centres, secteurs, statuts). Si tout est coché → décoche
  // tout, sinon coche tout. Cas "tout sauf intérim" : Tout cocher puis décocher Intérim.
  toggleFieldset(event) {
    const fieldset = event.target.closest("fieldset")
    if (!fieldset) return
    const boxes = fieldset.querySelectorAll('input[type="checkbox"]')
    const allChecked = Array.from(boxes).every((b) => b.checked)
    boxes.forEach((b) => { b.checked = !allChecked })
    this.criteriaChanged()
  }

  campaignChanged() {
    const hasCampaign = this.hasCampaignSelectTarget && this.campaignSelectTarget.value !== ""
    if (hasCampaign && this.hasScopeSelectTarget) {
      this.scopeSelectTarget.value = "b2b"
      this.scopeSelectTarget.disabled = true
    } else if (this.hasScopeSelectTarget) {
      this.scopeSelectTarget.disabled = false
    }
    this.generateName()
    this.schedulePreview()
  }

  // Bascule entre mode "Depuis campagne" et mode "Depuis critères".
  // Mutuellement exclusif : on cache la section opposée ET on reset son contenu pour
  // éviter qu'un select campagne fantôme persiste si on bascule sur critères (ou inversement).
  modeChanged(event) {
    const mode = event.target.value
    if (this.hasCampaignSectionTarget) {
      this.campaignSectionTarget.classList.toggle("hidden", mode !== "campaign")
    }
    if (this.hasCriteriaSectionTarget) {
      this.criteriaSectionTarget.classList.toggle("hidden", mode !== "criteria")
    }
    // Si on quitte le mode campaign → vider le select campaign pour ne pas la sauvegarder par erreur.
    if (mode !== "campaign" && this.hasCampaignSelectTarget) {
      this.campaignSelectTarget.value = ""
    }
    this._refreshModeCardStyles()
    this.generateName()
    this.schedulePreview()
  }

  _refreshModeCardStyles() {
    const campaignChecked = this.hasModeRadioCampaignTarget && this.modeRadioCampaignTarget.checked
    const criteriaChecked = this.hasModeRadioCriteriaTarget && this.modeRadioCriteriaTarget.checked
    if (this.hasModeCardCampaignTarget) {
      this.modeCardCampaignTarget.classList.toggle("border-gold", campaignChecked)
      this.modeCardCampaignTarget.classList.toggle("bg-gold/5", campaignChecked)
    }
    if (this.hasModeCardCriteriaTarget) {
      this.modeCardCriteriaTarget.classList.toggle("border-gold", criteriaChecked)
      this.modeCardCriteriaTarget.classList.toggle("bg-gold/5", criteriaChecked)
    }
  }

  generateName() {
    if (!this.hasNameInputTarget || !this._autoName) return

    if (this.hasCampaignSelectTarget && this.campaignSelectTarget.value) {
      const option = this.campaignSelectTarget.selectedOptions[0]
      const campaignName = option.text.replace(/\s*\(\d+\s+contacts\)$/, "").trim()
      this.nameInputTarget.value = campaignName
      return
    }

    const form = this.element.closest("form")
    if (!form) return

    const data = new FormData(form)
    const scope = this.scopeSelectTarget?.value || "b2b"
    const parts = [scope.toUpperCase()]

    // Critères multi (10/06/2026) : 1 valeur cochée → son nom ; plusieurs → "N x".
    const multiPart = (key, label) => {
      const values = data.getAll(`marketing_list[criteria][${key}][]`).filter(Boolean)
      if (values.length === 1) return values[0].replace(/_/g, " ")
      if (values.length > 1) return `${values.length} ${label}`
      return null
    }

    const companySizes = data.getAll("marketing_list[criteria][company_size][]").filter(Boolean)
    const category = data.get("marketing_list[criteria][category]")
    const sector = multiPart("sector", "secteurs")
    const leadStatus = multiPart("lead_status", "statuts") || multiPart("hs_lead_status", "statuts")

    if (companySizes.length === 1) parts.push(companySizes[0].toUpperCase())
    else if (companySizes.length > 1) parts.push(`${companySizes.length} tailles`)
    if (category) parts.push(category.charAt(0).toUpperCase() + category.slice(1))
    if (sector) parts.push(sector)
    if (leadStatus) parts.push(leadStatus)

    if (parts.length > 1) {
      this.nameInputTarget.value = "Liste " + parts.join(" · ")
    }
  }

  schedulePreview() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this.previewCount(), 500)
  }

  async previewCount() {
    if (!this.hasPreviewCountTarget) return

    const form = this.element.closest("form")
    if (!form) return

    const params = new URLSearchParams(new FormData(form))
    const scope = this.scopeSelectTarget?.value || "b2b"
    params.set("marketing_list[list_scope]", scope)

    try {
      const response = await fetch(
        `/marketing_lists/preview_count?${params.toString()}`,
        { headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" } }
      )
      const data = await response.json()
      // Story 2.G-final : affichage total + breakdown email (Brevo envoie qu'aux contacts
      // avec email valide). Bouton "Enrichir" si contacts sans email > 0.
      const total = data.total ?? data.count ?? 0
      const withEmail = data.with_email ?? 0
      const withoutEmail = data.without_email ?? 0
      const sLabel = total !== 1 ? "s" : ""
      let html = `<strong>${total} contact${sLabel}</strong>`
      if (total > 0) {
        html += ` · <span class="text-success">${withEmail} avec email</span>`
        if (withoutEmail > 0) {
          html += ` · <span class="text-text-muted">${withoutEmail} sans email</span>`
        }
      }
      this.previewCountTarget.innerHTML = html
      // Toggle bouton enrich si target dispo.
      if (this.hasEnrichButtonTarget) {
        this.enrichButtonTarget.classList.toggle("hidden", withoutEmail === 0)
        this.enrichButtonTarget.textContent = `Enrichir ${withoutEmail} contact${withoutEmail !== 1 ? "s" : ""} sans email`
      }
    } catch {
      this.previewCountTarget.textContent = "—"
    }
  }

  // Story 2.G-final (21/05/2026) — En mode campagne, route vers /campaigns/:id/enrich_all
  // (infra Apollo/FullEnrich existante). En mode critères, alert info (Sprint ultérieur).
  enrichMissingEmails(event) {
    event.preventDefault()
    const campaignId = this.hasCampaignSelectTarget && this.campaignSelectTarget.value
    if (campaignId) {
      window.location.href = `/campaigns/${campaignId}`
    } else {
      alert("Enrichissement depuis critères disponible après création de la liste. Crée la liste, puis clique 'Enrichir' sur la fiche.")
    }
  }
}
